import React, { useEffect, useMemo, useState } from "react";
import { AdminClient } from "../../api/generated/APIClient";
import {
  StructFieldInfo,
  StructInfo,
  getEntityFieldsWithBase,
  structDocumentationData,
} from "../../api/generated/StructDocs";
import { ANNOUNCE_MARKDOWN_LINE_LENGTH } from "../../constants/announce";
import { GiftReward, GiftRewardEditor } from "../common/GiftRewardEditor";
import { ConfirmDialog } from "./ConfirmDialog";
import "./DynamicForm.css";
import { EnumSelect, SelectedEnumValue } from "./EnumSelect";
import { MarkdownEditor } from "./MarkdownEditor";

// マスターセレクター統合機能のインポート
import { MasterDataSelectors } from "../../types/generated/MasterDataClient";

// Enum管理機能のインポート
import EnumManager from "../../utils/enumManager";

// 自動生成されたenum登録をインポート（副作用でenum登録が実行される）
import "../../types/generated/EnumRegistry";

// UUID生成ユーティリティ
import { generateUUIDv7 } from "../../utils/uuid";

// FieldMetadata型をStructDocs.tsの情報から作成するための型定義
interface FieldMetadata {
  name: string;
  type: string;
  jsonName: string;
  protoName: string;
  required: boolean;
  comment?: string;
  isAutoGenerated?: boolean;
  idDependencies?: string[];
  schemaVersion?: boolean;
}

// カスタムフィールドレンダラーの引数型
export interface CustomFieldRendererProps {
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  fieldName: string;
}

interface DynamicFormProps {
  entityName: string;
  initialData?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void;
  onCancel: () => void;
  mode?: "create" | "edit";
  userId?: string; // ルーティングからのUserID
  readOnlyFields?: string[]; // 読み取り専用フィールド（protoName）
  excludeFields?: string[]; // 除外フィールド（protoName）- フォームに表示しない
  renderBeforeField?: Record<string, () => React.ReactNode>; // 特定フィールドの前にカスタムコンテンツを挿入
  customFieldRenderers?: Record<
    string,
    (props: CustomFieldRendererProps) => React.ReactNode
  >; // フィールドをカスタムコンポーネントに置換
  delegateSubmit?: boolean; // trueの場合、内部でAPIを呼び出さず、onSubmitにデータを渡すだけ
  closeDialogOnOverlayClick?: boolean; // trueの場合、オーバーレイクリックでダイアログを閉じる（デフォルト: false）
  renderConfirmSummary?: (formData: Record<string, any>) => React.ReactNode; // 確認ダイアログに表示するカスタム要約
}

// ========================================
// 共通ヘルパー関数
// ========================================

// 配列型かどうかを判定
const isArrayType = (type: string): boolean => {
  return type.endsWith("[]");
};

// 配列の要素型を取得
const getArrayElementType = (type: string): string => {
  return type.replace("[]", "");
};

// enum型かどうかを判定
const isEnumType = (type: string): boolean => {
  if (isArrayType(type)) return false;
  return type.startsWith("consts.") || type.startsWith("Consts");
};

// 基本型かどうかを判定（enum型も含む、ただし配列型は除外）
const isBasicType = (type: string): boolean => {
  const basicTypes = ["string", "number", "boolean", "Date", "Duration"];
  const isMasterIdType = type.startsWith("master.Mst") && type.endsWith("ID");
  const isEntityIdType = type.startsWith("entity.") && type.endsWith("ID");
  const isSimpleIdType = /^[A-Z][a-zA-Z]*ID$/.test(type);

  if (isArrayType(type)) return false;

  return (
    basicTypes.includes(type) ||
    isEnumType(type) ||
    isMasterIdType ||
    isEntityIdType ||
    isSimpleIdType
  );
};

// 構造体型かどうかを判定
const isStructType = (type: string): boolean => {
  if (type.startsWith("master.Mst") && type.endsWith("ID")) return false;
  if (type.startsWith("entity.") && type.endsWith("ID")) return false;
  if (/^[A-Z][a-zA-Z]*ID$/.test(type)) return false;
  return (
    type.startsWith("entity.") &&
    !type.endsWith("[]") &&
    !isBasicType(type) &&
    !isEnumType(type)
  );
};

// 入力タイプを決定する共通関数
const getInputTypeForField = (type: string, protoName: string): string => {
  const lowerType = type.toLowerCase();

  if (lowerType === "boolean") return "checkbox";
  if (lowerType === "number") return "number";
  if (lowerType === "date") return "datetime-local";
  if (lowerType === "duration") return "time";

  if (lowerType === "string") {
    if (protoName.includes("email")) return "email";
    if (protoName.includes("password")) return "password";
    if (protoName.includes("url")) return "url";
    if (
      protoName.includes("time") ||
      protoName.endsWith("_time") ||
      protoName.endsWith("Time")
    )
      return "time";
    return "text";
  }

  return "text";
};

// デフォルト値を生成する共通関数
const createDefaultValueForType = (type: string, frontendType: string): any => {
  if (isArrayType(type)) return [];

  if (MasterDataSelectors.isMasterIdFieldByType(frontendType)) return 0;

  if (isEnumType(type)) return 0;

  if (isStructType(type)) return {};

  switch (type) {
    case "string":
      return "";
    case "number":
      return 0;
    case "boolean":
      return false;
    case "Date":
      return new Date().toISOString();
    case "Duration":
      return "00:00";
    default:
      if (type.endsWith("ID")) return "";
      return "";
  }
};

// enum型名からenum定数オブジェクトを取得
const getEnumObject = (enumType: string): Record<string, number> | null => {
  let elementType = getArrayElementType(enumType);
  // "Consts" プレフィックスを除去（例: "ConstsPlatformType" -> "PlatformType"）
  if (elementType.startsWith("Consts")) {
    elementType = elementType.substring(6);
  }
  // "consts." プレフィックスを除去（例: "consts.PlatformType" -> "PlatformType"）
  if (elementType.startsWith("consts.")) {
    elementType = elementType.substring(7);
  }
  return EnumManager.getEnumObject(elementType);
};

// enum型名から表示用のラベルフォーマッターを取得
const getEnumLabelFormatter = (enumType: string) => {
  let elementType = getArrayElementType(enumType);
  // "Consts" プレフィックスを除去（例: "ConstsPlatformType" -> "PlatformType"）
  if (elementType.startsWith("Consts")) {
    elementType = elementType.substring(6);
  }
  // "consts." プレフィックスを除去（例: "consts.PlatformType" -> "PlatformType"）
  if (elementType.startsWith("consts.")) {
    elementType = elementType.substring(7);
  }
  return EnumManager.getEnumLabelFormatter(elementType);
};

// enum数値から名前に変換
const convertEnumValueToName = (
  enumValue: number | string,
  enumType: string
): string => {
  const enumObject = getEnumObject(enumType);
  if (!enumObject) return String(enumValue);

  const numValue =
    typeof enumValue === "number" ? enumValue : Number(enumValue);
  if (!isNaN(numValue)) {
    for (const [key, val] of Object.entries(enumObject)) {
      if (val === numValue && isNaN(Number(key))) {
        return key;
      }
    }
  }
  return String(enumValue);
};

// enum名から数値に変換
const convertEnumNameToValue = (
  enumName: string,
  enumType: string
): number | null => {
  const enumObject = getEnumObject(enumType);
  if (!enumObject) return null;

  const numValue = Number(enumName);
  if (!isNaN(numValue)) return numValue;

  let result = enumObject[enumName] ?? null;

  if (result === null) {
    const availableKeys = Object.keys(enumObject).filter((key) =>
      isNaN(Number(key))
    );
    for (const key of availableKeys) {
      if (enumName.endsWith(`_${key}`) || enumName.endsWith(`_TYPE_${key}`)) {
        result = enumObject[key];
        break;
      }
    }
  }
  return result;
};

// datetime-local用の値変換
const formatDateTimeLocalValue = (value: any): string => {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch {
    return "";
  }
};

// time用の値変換
const formatTimeValue = (value: any): string => {
  if (!value) return "";
  try {
    if (typeof value === "string" && /^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
      return value.slice(0, 5);
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  } catch {
    return "";
  }
};

// エンティティ名を正規化（snake_case -> PascalCase、複数形 -> 単数形）
const normalizeEntityName = (name: string) => {
  // 複数形の "s" を除去（例: "users" -> "user", "devices" -> "device"）
  let singularName = name;
  if (name.endsWith("s") && !name.endsWith("ss")) {
    singularName = name.slice(0, -1);
  }

  return singularName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
};

// StructDocs.tsからエンティティ情報を取得
const getEntityStructInfo = (entityName: string): StructInfo | null => {
  const normalizedName = normalizeEntityName(entityName);
  return (
    structDocumentationData.find(
      (struct) =>
        struct.typeName === normalizedName &&
        struct.displayName === `entity.${normalizedName}`
    ) || null
  );
};

// 構造体情報を取得（型名から）
const getStructInfoByType = (structType: string): StructInfo | null => {
  const normalizedType = structType.replace(/^entity\./, "");
  return (
    structDocumentationData.find((struct) => {
      const normalizedStructType = struct.typeName.replace(
        /^Entity|^Service/,
        ""
      );
      const normalizedDisplayName = struct.displayName.replace(
        /^entity\.|^service\./,
        ""
      );
      return (
        struct.typeName === structType ||
        struct.displayName === structType ||
        normalizedStructType === normalizedType ||
        normalizedDisplayName === normalizedType ||
        struct.displayName.includes(structType) ||
        structType.includes(struct.typeName)
      );
    }) || null
  );
};

// StructFieldInfoをFieldMetadata形式に変換
const convertToFieldMetadata = (
  structField: StructFieldInfo
): FieldMetadata => {
  return {
    name: structField.name,
    type: structField.frontendType,
    jsonName: structField.protoName,
    protoName: structField.protoName,
    required: structField.isRequired,
    comment: structField.comment,
    isAutoGenerated: structField.protoName === "id",
    idDependencies: structField.protoName === "id" ? [] : undefined,
    schemaVersion: structField.protoName === "version",
  };
};

// 構造体のデフォルト値を生成
const createStructDefaultValue = (structType: string): any => {
  const structInfo = getStructInfoByType(structType);
  if (!structInfo) return {};

  const defaultValue: any = {};
  const fields = getEntityFieldsWithBase(structInfo);

  fields.forEach((field) => {
    defaultValue[field.protoName] = createDefaultValueForType(
      field.type,
      field.frontendType
    );
    if (isStructType(field.type)) {
      defaultValue[field.protoName] = createStructDefaultValue(field.type);
    }
  });

  return defaultValue;
};

export const DynamicForm: React.FC<DynamicFormProps> = ({
  entityName,
  initialData = {},
  onSubmit,
  onCancel,
  mode = "create",
  userId,
  readOnlyFields = [],
  excludeFields = [],
  renderBeforeField = {},
  customFieldRenderers = {},
  delegateSubmit = false,
  closeDialogOnOverlayClick = false,
  renderConfirmSummary,
}) => {
  const normalizedEntityName = normalizeEntityName(entityName);

  // エンティティメタデータを取得
  const entityMetadata = useMemo(() => {
    const structInfo = getEntityStructInfo(entityName);
    if (structInfo) {
      const fieldsWithBase = getEntityFieldsWithBase(structInfo);
      return {
        name: structInfo.displayName,
        fields: fieldsWithBase.map(convertToFieldMetadata),
        idComponent: structInfo.idComponent,
      };
    }
    return null;
  }, [normalizedEntityName]);

  const [formData, setFormData] = useState<Record<string, any>>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // スキーマバージョン指定フラグ（trueの場合、保存時にVersionのマイグレーションをスキップ）
  const [skipVersionMigration, setSkipVersionMigration] = useState(false);

  // ========================================
  // 汎用入力レンダリング関数
  // ========================================

  interface UnifiedFieldInputOptions {
    type: string;
    frontendType: string;
    protoName: string;
    value: any;
    onChange: (value: any) => void;
    placeholder?: string;
    className?: string;
    fieldName?: string;
    error?: string;
    id?: string;
  }

  // 汎用フィールド入力レンダリング
  const renderUnifiedFieldInput = (
    options: UnifiedFieldInputOptions
  ): React.ReactNode => {
    const {
      type,
      frontendType,
      protoName,
      value,
      onChange,
      placeholder,
      className = "",
      fieldName = "",
      error,
      id,
    } = options;

    // マスターIDフィールドの場合
    if (MasterDataSelectors.isMasterIdFieldByType(frontendType)) {
      const MasterSelector =
        MasterDataSelectors.getSelectorComponentByType(frontendType);
      if (MasterSelector) {
        return (
          <MasterSelector
            value={value}
            onChange={onChange}
            placeholder={placeholder || `${fieldName}を選択`}
            allowEmpty={true}
            className={`master-selector--compact ${className}`}
          />
        );
      }
      // フォールバック: 数値入力
      return (
        <input
          type="number"
          id={id}
          value={value || ""}
          onChange={(e) => {
            const newValue =
              e.target.value === "" ? "" : Number(e.target.value);
            onChange(newValue);
          }}
          className={`form-input ${className} ${error ? "error" : ""}`.trim()}
          placeholder={placeholder || `${fieldName}を入力 (マスターID)`}
        />
      );
    }

    // enum型フィールドの場合
    if (isEnumType(type)) {
      const enumObject = getEnumObject(type);
      if (enumObject) {
        return (
          <EnumSelect
            enumObject={enumObject}
            value={typeof value === "number" ? value : undefined}
            onChange={(selected: SelectedEnumValue) => onChange(selected.value)}
            labelFormatter={getEnumLabelFormatter(type)}
            placeholder={placeholder || `${fieldName}を選択`}
            className={`${className} ${error ? "error" : ""}`.trim()}
          />
        );
      }
      // フォールバック: 数値入力
      return (
        <input
          type="number"
          id={id}
          value={typeof value === "number" ? value : ""}
          onChange={(e) => {
            const newValue =
              e.target.value === "" ? "" : Number(e.target.value);
            onChange(newValue);
          }}
          className={`form-input ${className} ${error ? "error" : ""}`.trim()}
          placeholder={placeholder || `${fieldName}を入力 (enum値)`}
        />
      );
    }

    // 基本型フィールドの場合
    const inputType = getInputTypeForField(type, protoName);

    if (inputType === "checkbox") {
      return (
        <label className={`form-toggle-label ${className}`} htmlFor={id}>
          <div className="form-toggle-switch">
            <input
              type="checkbox"
              id={id}
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
            />
            <span className="form-toggle-slider"></span>
          </div>
          <span className="form-toggle-text">
            {Boolean(value) ? "ON" : "OFF"}
          </span>
        </label>
      );
    }

    // datetime-local / time / その他の入力
    let displayValue = value;
    if (inputType === "datetime-local" && value) {
      displayValue = formatDateTimeLocalValue(value);
    } else if (inputType === "time" && value) {
      displayValue = formatTimeValue(value);
    }

    return (
      <input
        type={inputType}
        id={id}
        value={displayValue ?? ""}
        onChange={(e) => {
          let newValue: any;
          if (inputType === "number") {
            newValue = e.target.value === "" ? "" : Number(e.target.value);
          } else if (inputType === "datetime-local") {
            if (e.target.value) {
              const localDateString = e.target.value + ":00";
              const localDate = new Date(localDateString);
              newValue = localDate.toISOString();
            } else {
              newValue = "";
            }
          } else {
            newValue = e.target.value;
          }
          onChange(newValue);
        }}
        className={`form-input ${className} ${error ? "error" : ""}`.trim()}
        placeholder={placeholder || `${fieldName}を入力してください`}
      />
    );
  };

  // ========================================
  // 汎用配列操作関数
  // ========================================

  // 配列要素を追加（パスベース）
  const addArrayItemAtPath = (
    basePath: string[],
    arrayKey: string,
    elementType: string,
    elementFrontendType: string
  ) => {
    let defaultValue: any;
    if (MasterDataSelectors.isMasterIdFieldByType(elementFrontendType)) {
      defaultValue = 0;
    } else if (isEnumType(elementType)) {
      defaultValue = 0;
    } else if (isBasicType(elementType)) {
      defaultValue = createDefaultValueForType(
        elementType,
        elementFrontendType
      );
    } else {
      defaultValue = createStructDefaultValue(elementType);
    }

    setFormData((prev) => {
      const newData = { ...prev };
      let target: any = newData;

      // パスをたどって対象オブジェクトに到達
      for (const key of basePath) {
        if (target[key] === undefined) {
          target[key] = {};
        } else {
          target[key] = { ...target[key] };
        }
        target = target[key];
      }

      // 配列に要素を追加
      target[arrayKey] = [...(target[arrayKey] || []), defaultValue];

      return newData;
    });
  };

  // 配列要素を削除（パスベース）
  const removeArrayItemAtPath = (
    basePath: string[],
    arrayKey: string,
    index: number
  ) => {
    setFormData((prev) => {
      const newData = { ...prev };
      let target: any = newData;

      for (const key of basePath) {
        target[key] = { ...target[key] };
        target = target[key];
      }

      target[arrayKey] = (target[arrayKey] || []).filter(
        (_: any, i: number) => i !== index
      );

      return newData;
    });
  };

  // 配列要素を更新（パスベース）
  const updateArrayItemAtPath = (
    basePath: string[],
    arrayKey: string,
    index: number,
    newValue: any
  ) => {
    setFormData((prev) => {
      const newData = { ...prev };
      let target: any = newData;

      for (const key of basePath) {
        target[key] = { ...target[key] };
        target = target[key];
      }

      target[arrayKey] = (target[arrayKey] || []).map((item: any, i: number) =>
        i === index ? newValue : item
      );

      return newData;
    });
  };

  // ネストした値を更新
  const updateNestedValue = (basePath: string[], key: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev };
      let target: any = newData;

      for (const pathKey of basePath) {
        if (target[pathKey] === undefined) {
          target[pathKey] = {};
        } else {
          target[pathKey] = { ...target[pathKey] };
        }
        target = target[pathKey];
      }

      target[key] = value;
      return newData;
    });
  };

  // ========================================
  // 汎用配列要素レンダリング
  // ========================================

  interface ArrayItemRenderOptions {
    basePath: string[];
    arrayKey: string;
    index: number;
    item: any;
    elementType: string;
    elementFrontendType: string;
    fieldName: string;
  }

  // 配列要素をレンダリング（基本型/enum/マスターID共通）
  const renderArrayItemInput = (
    options: ArrayItemRenderOptions
  ): React.ReactNode => {
    const {
      basePath,
      arrayKey,
      index,
      item,
      elementType,
      elementFrontendType,
      fieldName,
    } = options;

    return renderUnifiedFieldInput({
      type: elementType,
      frontendType: elementFrontendType,
      protoName: "",
      value: item,
      onChange: (newValue) =>
        updateArrayItemAtPath(basePath, arrayKey, index, newValue),
      placeholder: `${fieldName} ${index + 1}を入力`,
      fieldName: `${fieldName} ${index + 1}`,
      className: "array-input",
    });
  };

  // ========================================
  // 構造体フィールドレンダリング（統合版）
  // ========================================

  interface StructFieldRenderOptions {
    basePath: string[];
    structInfo: StructInfo;
    currentValue: any;
    depth?: number;
  }

  // 構造体のフィールドをレンダリング
  const renderStructFields = (
    options: StructFieldRenderOptions
  ): React.ReactNode => {
    const { basePath, structInfo, currentValue, depth = 0 } = options;
    const structFields = getEntityFieldsWithBase(structInfo).map(
      convertToFieldMetadata
    );

    return (
      <div className={`struct-fields depth-${depth}`}>
        {structFields.map((subField) => {
          const subStructField = getEntityFieldsWithBase(structInfo).find(
            (sf) => sf.protoName === subField.protoName
          );
          if (!subStructField) return null;

          const isSubFieldEnum = isEnumType(subStructField.type);
          const isSubFieldArray = isArrayType(subStructField.type);
          const isSubFieldStruct = isStructType(subStructField.type);
          const isSubFieldMaster = MasterDataSelectors.isMasterIdFieldByType(
            subStructField.frontendType
          );

          return (
            <div key={subField.protoName} className="struct-field">
              <label className="form-label struct-field-label">
                {subField.name}
                {subField.required && <span className="required">*</span>}
                {isSubFieldEnum && (
                  <span className="enum-indicator">(enum)</span>
                )}
                {isSubFieldArray && (
                  <span className="array-indicator">(配列)</span>
                )}
                {isSubFieldStruct && (
                  <span className="struct-indicator">(構造体)</span>
                )}
              </label>

              {subField.comment && (
                <div className="field-comment">{subField.comment}</div>
              )}

              {isSubFieldArray
                ? renderNestedArrayField({
                    basePath,
                    arrayKey: subField.protoName,
                    structField: subStructField,
                    fieldName: subField.name,
                    currentValue: currentValue[subField.protoName] || [],
                    depth: depth + 1,
                  })
                : isSubFieldStruct
                ? renderNestedStructField({
                    basePath,
                    structKey: subField.protoName,
                    structField: subStructField,
                    fieldName: subField.name,
                    currentValue: currentValue[subField.protoName] || {},
                    depth: depth + 1,
                  })
                : renderUnifiedFieldInput({
                    type: subStructField.type,
                    frontendType: subStructField.frontendType,
                    protoName: subField.protoName,
                    value: currentValue[subField.protoName],
                    onChange: (value) =>
                      updateNestedValue(basePath, subField.protoName, value),
                    placeholder: `${subField.name}を入力`,
                    fieldName: subField.name,
                    className: "struct-input",
                  })}
            </div>
          );
        })}
      </div>
    );
  };

  // ネストした構造体フィールドをレンダリング
  interface NestedStructFieldOptions {
    basePath: string[];
    structKey: string;
    structField: StructFieldInfo;
    fieldName: string;
    currentValue: any;
    depth?: number;
  }

  const renderNestedStructField = (
    options: NestedStructFieldOptions
  ): React.ReactNode => {
    const {
      basePath,
      structKey,
      structField,
      fieldName,
      currentValue,
      depth = 0,
    } = options;

    const nestedStructInfo = getStructInfoByType(structField.type);
    if (!nestedStructInfo) {
      return (
        <div className="nested-struct-placeholder">
          <span>構造体 {structField.type} の情報が見つかりません</span>
        </div>
      );
    }

    return (
      <div className="nested-struct-container">
        {renderStructFields({
          basePath: [...basePath, structKey],
          structInfo: nestedStructInfo,
          currentValue,
          depth,
        })}
      </div>
    );
  };

  // ネストした配列フィールドをレンダリング
  interface NestedArrayFieldOptions {
    basePath: string[];
    arrayKey: string;
    structField: StructFieldInfo;
    fieldName: string;
    currentValue: any[];
    depth?: number;
  }

  const renderNestedArrayField = (
    options: NestedArrayFieldOptions
  ): React.ReactNode => {
    const {
      basePath,
      arrayKey,
      structField,
      fieldName,
      currentValue,
      depth = 0,
    } = options;

    const elementType = getArrayElementType(structField.type);
    const elementFrontendType = getArrayElementType(structField.frontendType);
    const isBasicElement = isBasicType(elementType);

    return (
      <div className="nested-array-container">
        {currentValue.map((item: any, index: number) => (
          <div key={index} className="nested-array-item">
            <div className="nested-array-item-header">
              <span className="nested-array-item-index">#{index + 1}</span>
              <button
                type="button"
                className="btn btn-danger btn-small"
                onClick={() => removeArrayItemAtPath(basePath, arrayKey, index)}
                title="削除"
              >
                ×
              </button>
            </div>

            <div className="nested-array-item-content">
              {isBasicElement
                ? renderArrayItemInput({
                    basePath,
                    arrayKey,
                    index,
                    item,
                    elementType,
                    elementFrontendType,
                    fieldName,
                  })
                : renderStructArrayItem({
                    basePath,
                    arrayKey,
                    index,
                    item,
                    elementType,
                    depth: depth + 1,
                  })}
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-secondary btn-add-nested-array-item"
          onClick={() =>
            addArrayItemAtPath(
              basePath,
              arrayKey,
              elementType,
              elementFrontendType
            )
          }
        >
          + {fieldName}を追加
        </button>
      </div>
    );
  };

  // 構造体配列要素をレンダリング
  interface StructArrayItemOptions {
    basePath: string[];
    arrayKey: string;
    index: number;
    item: any;
    elementType: string;
    depth?: number;
  }

  const renderStructArrayItem = (
    options: StructArrayItemOptions
  ): React.ReactNode => {
    const { basePath, arrayKey, index, item, elementType, depth = 0 } = options;

    const elementStructInfo = getStructInfoByType(elementType);
    if (!elementStructInfo) {
      return (
        <div className="struct-array-item-placeholder">
          <span>構造体 {elementType} の情報が見つかりません</span>
        </div>
      );
    }

    const structFields = getEntityFieldsWithBase(elementStructInfo).map(
      convertToFieldMetadata
    );

    return (
      <div className="struct-array-item">
        <div className="struct-array-item-header">
          <span className="struct-array-item-title">
            {elementStructInfo.jpName || elementStructInfo.typeName} #
            {index + 1}
          </span>
        </div>
        <div className="struct-array-item-content">
          {structFields.map((subField) => {
            const subStructField = getEntityFieldsWithBase(
              elementStructInfo
            ).find((sf) => sf.protoName === subField.protoName);
            if (!subStructField) return null;

            const isSubFieldEnum = isEnumType(subStructField.type);
            const isSubFieldArray = isArrayType(subStructField.type);
            const isSubFieldStruct = isStructType(subStructField.type);

            // 配列内要素の更新用ヘルパー
            const updateStructArrayItemField = (
              fieldKey: string,
              value: any
            ) => {
              const updatedItem = { ...item, [fieldKey]: value };
              updateArrayItemAtPath(basePath, arrayKey, index, updatedItem);
            };

            return (
              <div key={subField.protoName} className="struct-field">
                <label className="form-label struct-field-label">
                  {subField.name}
                  {subField.required && <span className="required">*</span>}
                  {isSubFieldEnum && (
                    <span className="enum-indicator">(enum)</span>
                  )}
                  {isSubFieldArray && (
                    <span className="array-indicator">(配列)</span>
                  )}
                  {isSubFieldStruct && (
                    <span className="struct-indicator">(構造体)</span>
                  )}
                </label>

                {subField.comment && (
                  <div className="field-comment">{subField.comment}</div>
                )}

                {isSubFieldArray
                  ? renderStructArraySubArray({
                      basePath,
                      parentArrayKey: arrayKey,
                      parentIndex: index,
                      parentItem: item,
                      subField,
                      subStructField,
                      depth: depth + 1,
                    })
                  : isSubFieldStruct
                  ? renderStructArrayNestedStruct({
                      basePath,
                      parentArrayKey: arrayKey,
                      parentIndex: index,
                      parentItem: item,
                      subField,
                      subStructField,
                      depth: depth + 1,
                    })
                  : renderUnifiedFieldInput({
                      type: subStructField.type,
                      frontendType: subStructField.frontendType,
                      protoName: subField.protoName,
                      value: item[subField.protoName],
                      onChange: (value) =>
                        updateStructArrayItemField(subField.protoName, value),
                      placeholder: `${subField.name}を入力`,
                      fieldName: subField.name,
                      className: "struct-input",
                    })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // 構造体配列内の配列サブフィールド
  interface StructArraySubArrayOptions {
    basePath: string[];
    parentArrayKey: string;
    parentIndex: number;
    parentItem: any;
    subField: FieldMetadata;
    subStructField: StructFieldInfo;
    depth?: number;
  }

  const renderStructArraySubArray = (
    options: StructArraySubArrayOptions
  ): React.ReactNode => {
    const {
      basePath,
      parentArrayKey,
      parentIndex,
      parentItem,
      subField,
      subStructField,
      depth = 0,
    } = options;

    const arrayValue = parentItem[subField.protoName] || [];
    const elementType = getArrayElementType(subStructField.type);
    const elementFrontendType = getArrayElementType(
      subStructField.frontendType
    );
    const isBasicElement = isBasicType(elementType);

    const addSubItem = () => {
      let defaultValue: any;
      if (MasterDataSelectors.isMasterIdFieldByType(elementFrontendType)) {
        defaultValue = 0;
      } else if (isEnumType(elementType)) {
        defaultValue = 0;
      } else if (isBasicElement) {
        defaultValue = createDefaultValueForType(
          elementType,
          elementFrontendType
        );
      } else {
        defaultValue = createStructDefaultValue(elementType);
      }
      const updatedItem = {
        ...parentItem,
        [subField.protoName]: [...arrayValue, defaultValue],
      };
      updateArrayItemAtPath(basePath, parentArrayKey, parentIndex, updatedItem);
    };

    const removeSubItem = (subIndex: number) => {
      const updatedItem = {
        ...parentItem,
        [subField.protoName]: arrayValue.filter(
          (_: any, i: number) => i !== subIndex
        ),
      };
      updateArrayItemAtPath(basePath, parentArrayKey, parentIndex, updatedItem);
    };

    const updateSubItem = (subIndex: number, value: any) => {
      const updatedItem = {
        ...parentItem,
        [subField.protoName]: arrayValue.map((item: any, i: number) =>
          i === subIndex ? value : item
        ),
      };
      updateArrayItemAtPath(basePath, parentArrayKey, parentIndex, updatedItem);
    };

    return (
      <div className="struct-sub-array-container">
        {arrayValue.map((item: any, subIndex: number) => (
          <div key={subIndex} className="struct-sub-array-item">
            <div className="struct-sub-array-item-header">
              <span className="struct-sub-array-item-index">
                #{subIndex + 1}
              </span>
              <button
                type="button"
                className="btn btn-danger btn-small"
                onClick={() => removeSubItem(subIndex)}
                title="削除"
              >
                ×
              </button>
            </div>

            <div className="struct-sub-array-item-content">
              {isBasicElement
                ? renderUnifiedFieldInput({
                    type: elementType,
                    frontendType: elementFrontendType,
                    protoName: "",
                    value: item,
                    onChange: (value) => updateSubItem(subIndex, value),
                    placeholder: `${subField.name} ${subIndex + 1}`,
                    fieldName: `${subField.name} ${subIndex + 1}`,
                    className: "struct-sub-array-input",
                  })
                : (() => {
                    // ネスト構造体配列のレンダリング（深い階層）
                    const nestedStructInfo = getStructInfoByType(elementType);
                    if (!nestedStructInfo) {
                      return <span>構造体情報が見つかりません</span>;
                    }
                    const nestedFields = getEntityFieldsWithBase(
                      nestedStructInfo
                    ).map(convertToFieldMetadata);
                    return (
                      <div className="nested-struct-in-array">
                        {nestedFields.map((nf) => {
                          const nsf = getEntityFieldsWithBase(
                            nestedStructInfo
                          ).find((x) => x.protoName === nf.protoName);
                          if (!nsf) return null;
                          return (
                            <div key={nf.protoName} className="nested-field">
                              <label className="form-label">{nf.name}</label>
                              {renderUnifiedFieldInput({
                                type: nsf.type,
                                frontendType: nsf.frontendType,
                                protoName: nf.protoName,
                                value: item[nf.protoName],
                                onChange: (value) => {
                                  updateSubItem(subIndex, {
                                    ...item,
                                    [nf.protoName]: value,
                                  });
                                },
                                fieldName: nf.name,
                                className: "nested-input",
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-secondary btn-add-struct-sub-array-item"
          onClick={addSubItem}
        >
          + {subField.name}を追加
        </button>
      </div>
    );
  };

  // 構造体配列内のネスト構造体
  interface StructArrayNestedStructOptions {
    basePath: string[];
    parentArrayKey: string;
    parentIndex: number;
    parentItem: any;
    subField: FieldMetadata;
    subStructField: StructFieldInfo;
    depth?: number;
  }

  const renderStructArrayNestedStruct = (
    options: StructArrayNestedStructOptions
  ): React.ReactNode => {
    const {
      basePath,
      parentArrayKey,
      parentIndex,
      parentItem,
      subField,
      subStructField,
      depth = 0,
    } = options;

    const nestedValue = parentItem[subField.protoName] || {};
    const nestedStructInfo = getStructInfoByType(subStructField.type);

    if (!nestedStructInfo) {
      return (
        <div className="nested-struct-placeholder">
          <span>構造体 {subStructField.type} の情報が見つかりません</span>
        </div>
      );
    }

    const nestedFields = getEntityFieldsWithBase(nestedStructInfo).map(
      convertToFieldMetadata
    );

    const updateNestedField = (fieldKey: string, value: any) => {
      const updatedNestedValue = { ...nestedValue, [fieldKey]: value };
      const updatedItem = {
        ...parentItem,
        [subField.protoName]: updatedNestedValue,
      };
      updateArrayItemAtPath(basePath, parentArrayKey, parentIndex, updatedItem);
    };

    return (
      <div className="nested-struct-container">
        <div className="nested-struct-fields">
          {nestedFields.map((nf) => {
            const nsf = getEntityFieldsWithBase(nestedStructInfo).find(
              (x) => x.protoName === nf.protoName
            );
            if (!nsf) return null;

            const isNfArray = isArrayType(nsf.type);
            const isNfStruct = isStructType(nsf.type);

            return (
              <div key={nf.protoName} className="nested-struct-field">
                <label className="form-label nested-struct-field-label">
                  {nf.name}
                  {nf.required && <span className="required">*</span>}
                </label>

                {nf.comment && (
                  <div className="field-comment">{nf.comment}</div>
                )}

                {isNfArray ? (
                  // 再帰的に配列をレンダリング
                  (() => {
                    const arrValue = nestedValue[nf.protoName] || [];
                    const elemType = getArrayElementType(nsf.type);
                    const elemFType = getArrayElementType(nsf.frontendType);
                    const isBasicElem = isBasicType(elemType);

                    const addNestedArrayItem = () => {
                      let defVal: any;
                      if (
                        MasterDataSelectors.isMasterIdFieldByType(elemFType)
                      ) {
                        defVal = 0;
                      } else if (isEnumType(elemType)) {
                        defVal = 0;
                      } else if (isBasicElem) {
                        defVal = createDefaultValueForType(elemType, elemFType);
                      } else {
                        defVal = createStructDefaultValue(elemType);
                      }
                      updateNestedField(nf.protoName, [...arrValue, defVal]);
                    };

                    return (
                      <div className="deep-nested-array">
                        {arrValue.map((arrItem: any, arrIdx: number) => (
                          <div key={arrIdx} className="deep-nested-array-item">
                            <div className="deep-nested-array-item-header">
                              <span>#{arrIdx + 1}</span>
                              <button
                                type="button"
                                className="btn btn-danger btn-small"
                                onClick={() => {
                                  const newArr = arrValue.filter(
                                    (_: any, i: number) => i !== arrIdx
                                  );
                                  updateNestedField(nf.protoName, newArr);
                                }}
                              >
                                ×
                              </button>
                            </div>
                            {renderUnifiedFieldInput({
                              type: elemType,
                              frontendType: elemFType,
                              protoName: "",
                              value: arrItem,
                              onChange: (v) => {
                                const newArr = arrValue.map(
                                  (x: any, i: number) => (i === arrIdx ? v : x)
                                );
                                updateNestedField(nf.protoName, newArr);
                              },
                              fieldName: `${nf.name} ${arrIdx + 1}`,
                            })}
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-secondary btn-small"
                          onClick={addNestedArrayItem}
                        >
                          + {nf.name}を追加
                        </button>
                      </div>
                    );
                  })()
                ) : isNfStruct ? (
                  // 再帰的に構造体をレンダリング（深い階層）
                  <span>深いネスト構造体は未対応</span>
                ) : (
                  renderUnifiedFieldInput({
                    type: nsf.type,
                    frontendType: nsf.frontendType,
                    protoName: nf.protoName,
                    value: nestedValue[nf.protoName],
                    onChange: (value) => updateNestedField(nf.protoName, value),
                    fieldName: nf.name,
                    className: "nested-struct-input",
                  })
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ========================================
  // トップレベルフィールドレンダリング
  // ========================================

  const handleInputChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  // 配列フィールドのレンダリング（トップレベル）
  const renderArrayField = (
    field: FieldMetadata,
    structField: StructFieldInfo
  ) => {
    const arrayValue = formData[field.protoName] || [];
    const elementType = getArrayElementType(structField.type);
    const elementFrontendType = getArrayElementType(structField.frontendType);
    const isBasicElement = isBasicType(elementType);
    const error = errors[field.protoName];

    return (
      <div key={field.protoName} className="form-field array-field">
        <label className="form-label">
          {field.name}
          {field.required && <span className="required">*</span>}
          <span className="array-indicator">(配列)</span>
        </label>

        {field.comment && <div className="field-comment">{field.comment}</div>}

        <div className="array-container">
          {arrayValue.map((item: any, index: number) => (
            <div key={index} className="array-item">
              <div className="array-item-header">
                <span className="array-item-index">#{index + 1}</span>
                <button
                  type="button"
                  className="btn btn-danger btn-small"
                  onClick={() =>
                    removeArrayItemAtPath([], field.protoName, index)
                  }
                  title="削除"
                >
                  ×
                </button>
              </div>

              <div className="array-item-content">
                {isBasicElement
                  ? renderArrayItemInput({
                      basePath: [],
                      arrayKey: field.protoName,
                      index,
                      item,
                      elementType,
                      elementFrontendType,
                      fieldName: field.name,
                    })
                  : renderStructArrayItem({
                      basePath: [],
                      arrayKey: field.protoName,
                      index,
                      item,
                      elementType,
                    })}
              </div>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-secondary btn-add-array-item"
            onClick={() =>
              addArrayItemAtPath(
                [],
                field.protoName,
                elementType,
                elementFrontendType
              )
            }
          >
            + 要素を追加
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  // 単一構造体フィールドのレンダリング（トップレベル）
  const renderStructField = (
    field: FieldMetadata,
    structField: StructFieldInfo
  ) => {
    const error = errors[field.protoName];
    const structInfo = getStructInfoByType(structField.type);

    // Reward 型のフィールドは GiftRewardEditor を使用
    if (structField.type.includes("Reward")) {
      const currentValue = (formData[field.protoName] as GiftReward) || {
        items: [],
        characters: [],
        ornaments: [],
        achievements: [],
      };

      return (
        <div key={field.protoName} className="form-field struct-field">
          <label className="form-label">
            {field.name}
            {field.required && <span className="required">*</span>}
          </label>

          {field.comment && (
            <div className="field-comment">{field.comment}</div>
          )}

          <GiftRewardEditor
            value={currentValue}
            onChange={(reward) => handleInputChange(field.protoName, reward)}
          />

          {error && <div className="error-message">{error}</div>}
        </div>
      );
    }

    if (!structInfo) {
      return (
        <div key={field.protoName} className="form-field struct-field">
          <label className="form-label">
            {field.name}
            {field.required && <span className="required">*</span>}
            <span className="struct-indicator">(構造体 - 未対応)</span>
          </label>
          <div className="struct-placeholder">
            <span>構造体 {structField.type} の情報が見つかりません</span>
          </div>
        </div>
      );
    }

    const currentValue = formData[field.protoName] || {};

    return (
      <div key={field.protoName} className="form-field struct-field">
        <label className="form-label">
          {field.name}
          {field.required && <span className="required">*</span>}
          <span className="struct-indicator">(構造体)</span>
        </label>

        {field.comment && <div className="field-comment">{field.comment}</div>}

        <div className="struct-container">
          <div className="struct-item">
            <div className="struct-item-content">
              {renderStructFields({
                basePath: [field.protoName],
                structInfo,
                currentValue,
                depth: 0,
              })}
            </div>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  // マスターIDフィールドのレンダリング（トップレベル）
  const renderMasterIdField = (
    field: FieldMetadata,
    structField: StructFieldInfo
  ) => {
    const error = errors[field.protoName];
    const currentValue = formData[field.protoName];

    return (
      <div key={field.protoName} className="form-field">
        <label htmlFor={field.protoName} className="form-label">
          {field.name}
          {field.required && <span className="required">*</span>}
          <span className="master-id-indicator">(マスター選択)</span>
        </label>

        {field.comment && <div className="field-comment">{field.comment}</div>}

        {renderUnifiedFieldInput({
          type: structField.type,
          frontendType: structField.frontendType,
          protoName: field.protoName,
          value: currentValue,
          onChange: (value) => handleInputChange(field.protoName, value),
          placeholder: `${field.name}を選択してください`,
          fieldName: field.name,
          error,
          id: field.protoName,
          className: error ? "master-selector--error" : "",
        })}

        {error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  // 通常フィールドのレンダリング
  const renderField = (field: FieldMetadata) => {
    // 除外フィールドはスキップ
    if (excludeFields.includes(field.protoName)) {
      return null;
    }

    // カスタムフィールドレンダラーがあれば使用
    if (customFieldRenderers[field.protoName]) {
      const value = formData[field.protoName];
      return (
        <div key={field.protoName} className="form-field custom-field">
          <label htmlFor={field.protoName} className="form-label">
            {field.name}
            {field.required && <span className="required">*</span>}
          </label>
          {field.comment && (
            <div className="field-comment">{field.comment}</div>
          )}
          {customFieldRenderers[field.protoName]({
            value,
            onChange: (newValue) =>
              handleFieldChange(field.protoName, newValue),
            disabled: false,
            fieldName: field.protoName,
          })}
        </div>
      );
    }

    const isAutoGenerated = field.protoName === "id";
    const value =
      formData[field.protoName] !== undefined ? formData[field.protoName] : "";
    const error = errors[field.protoName];

    const isUserIdField = field.protoName === "userId";
    const isUserIdReadonly = isUserIdField && userId;
    const isExplicitlyReadOnly = readOnlyFields.includes(field.protoName);
    // Versionフィールドはスキーマバージョン指定がfalseの時は読み取り専用
    const isVersionReadonly =
      field.protoName === "version" && !skipVersionMigration;
    const isReadonly =
      isAutoGenerated ||
      isUserIdReadonly ||
      isExplicitlyReadOnly ||
      isVersionReadonly;

    const structInfo = getEntityStructInfo(entityName);
    const structField = structInfo
      ? getEntityFieldsWithBase(structInfo).find(
          (sf) => sf.protoName === field.protoName
        )
      : null;

    // 配列型フィールド
    if (structField && isArrayType(structField.type)) {
      return renderArrayField(field, structField);
    }

    // マスターIDフィールド
    if (
      structField &&
      MasterDataSelectors.isMasterIdFieldByType(structField.frontendType) &&
      !isReadonly
    ) {
      return renderMasterIdField(field, structField);
    }

    // enum型フィールド（structFieldがある場合はstructField.typeを、ない場合はfield.typeを使用）
    const enumType = structField?.type || field.type;
    const enumFrontendType = structField?.frontendType || field.type;
    if (isEnumType(enumType) && !isReadonly) {
      return (
        <div key={field.protoName} className="form-field">
          <label htmlFor={field.protoName} className="form-label">
            {field.name}
            {field.required && <span className="required">*</span>}
            <span className="enum-indicator">(enum)</span>
          </label>
          {field.comment && (
            <div className="field-comment">{field.comment}</div>
          )}
          {renderUnifiedFieldInput({
            type: enumType,
            frontendType: enumFrontendType,
            protoName: field.protoName,
            value: formData[field.protoName],
            onChange: (value) => handleInputChange(field.protoName, value),
            placeholder: `${field.name}を選択してください`,
            fieldName: field.name,
            error,
            id: field.protoName,
          })}
          {error && <div className="error-message">{error}</div>}
        </div>
      );
    }

    // 構造体フィールド
    if (structField && isStructType(structField.type) && !isReadonly) {
      return renderStructField(field, structField);
    }

    // 読み取り専用フィールド
    if (isReadonly) {
      return (
        <div key={field.protoName} className="form-field">
          <label htmlFor={field.protoName} className="form-label">
            {field.name}
            {isUserIdReadonly && (
              <span className="readonly-indicator">(固定値)</span>
            )}
            {isAutoGenerated && !isUserIdReadonly && (
              <span className="readonly-indicator">(自動生成)</span>
            )}
            {isVersionReadonly && (
              <span className="readonly-indicator">(自動マイグレート)</span>
            )}
            {isExplicitlyReadOnly &&
              !isAutoGenerated &&
              !isUserIdReadonly &&
              !isVersionReadonly && (
                <span className="readonly-indicator">(固定値)</span>
              )}
          </label>
          {field.comment && (
            <div className="field-comment">{field.comment}</div>
          )}
          <input
            type="text"
            id={field.protoName}
            value={
              // Versionフィールドでスキーマバージョン指定がfalseの場合は空表示
              isVersionReadonly && !value
                ? ""
                : structField && isEnumType(structField.type)
                ? convertEnumValueToName(value, structField.type)
                : getInputTypeForField(field.type, field.protoName) ===
                    "datetime-local" && value
                ? formatDateTimeLocalValue(value)
                : getInputTypeForField(field.type, field.protoName) ===
                    "time" && value
                ? formatTimeValue(value)
                : String(value)
            }
            placeholder={isVersionReadonly ? "保存時に自動設定" : undefined}
            readOnly
            className="form-input readonly"
          />
        </div>
      );
    }

    // Markdown エディタ（お知らせ用）
    if (
      field.type === "string" &&
      field.protoName === "content" &&
      entityName === "announce"
    ) {
      return (
        <div key={field.protoName} className="form-field">
          <label htmlFor={field.protoName} className="form-label">
            {field.name}
            {field.required && <span className="required">*</span>}
          </label>
          {field.comment && (
            <div className="field-comment">{field.comment}</div>
          )}
          <MarkdownEditor
            value={value || ""}
            onChange={(newValue) =>
              handleInputChange(field.protoName, newValue)
            }
            lineLength={ANNOUNCE_MARKDOWN_LINE_LENGTH}
            placeholder={`${field.name}をMarkdown形式で入力してください`}
          />
          {error && <div className="error-message">{error}</div>}
        </div>
      );
    }

    // テキストエリア（description系）
    if (field.type === "string" && field.protoName.includes("description")) {
      return (
        <div key={field.protoName} className="form-field">
          <label htmlFor={field.protoName} className="form-label">
            {field.name}
            {field.required && <span className="required">*</span>}
          </label>
          {field.comment && (
            <div className="field-comment">{field.comment}</div>
          )}
          <textarea
            id={field.protoName}
            value={value}
            onChange={(e) => handleInputChange(field.protoName, e.target.value)}
            className={`form-textarea ${error ? "error" : ""}`}
            rows={3}
            placeholder={`${field.name}を入力してください`}
          />
          {error && <div className="error-message">{error}</div>}
        </div>
      );
    }

    // 通常の入力フィールド
    return (
      <div key={field.protoName} className="form-field">
        <label htmlFor={field.protoName} className="form-label">
          {field.name}
          {field.required && <span className="required">*</span>}
        </label>
        {field.comment && <div className="field-comment">{field.comment}</div>}
        {renderUnifiedFieldInput({
          type: structField?.type || field.type,
          frontendType: structField?.frontendType || field.type,
          protoName: field.protoName,
          value,
          onChange: (newValue) => handleInputChange(field.protoName, newValue),
          placeholder: `${field.name}を入力してください`,
          fieldName: field.name,
          error,
          id: field.protoName,
        })}
        {error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  // ========================================
  // ID生成・API呼び出し
  // ========================================

  const generateID = (
    dependencies: string[],
    data: Record<string, any>
  ): string => {
    const values = dependencies
      .map((dep) => data[dep])
      .filter((val) => val !== undefined && val !== null && val !== "");
    if (values.length === 0) return "自動生成";
    return values.join("_");
  };

  const callEntityAPI = async (
    operation: "create" | "update" | "delete",
    data: Record<string, any>,
    ids?: string[]
  ) => {
    const entityCamelCase =
      normalizedEntityName.charAt(0).toLowerCase() +
      normalizedEntityName.slice(1);
    const methodName = `${
      operation.charAt(0).toUpperCase() + operation.slice(1)
    }${entityCamelCase.charAt(0).toUpperCase() + entityCamelCase.slice(1)}`;

    const apiMethod = (AdminClient as any)[methodName];
    if (!apiMethod || typeof apiMethod !== "function") {
      throw new Error(`gRPCメソッド ${methodName} が見つかりません`);
    }

    let apiArgs: any;
    if (operation === "delete" && ids) {
      apiArgs = { ids };
    } else {
      const entityArrayKey = `${entityCamelCase}s`;
      apiArgs = {
        [entityArrayKey]: [data],
        skipVersionMigration, // スキーマバージョン指定フラグがtrueの場合、Versionのマイグレーションをスキップ
      };
    }

    return await apiMethod(apiArgs);
  };

  const sendCreateRequest = async (data: Record<string, any>) => {
    try {
      setIsSubmitting(true);
      const result = await callEntityAPI("create", data);
      if (result.error) {
        throw new Error(result.error);
      } else if (result.data !== undefined) {
        const createdData =
          result.data && Object.keys(result.data).length > 0
            ? result.data
            : data;
        onSubmit(createdData);
      } else {
        throw new Error("作成に失敗しました");
      }
    } catch (error) {
      console.error("Create request failed:", error);
      alert(
        `作成に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    } finally {
      setIsSubmitting(false);
      setShowConfirmDialog(false);
    }
  };

  const sendUpdateRequest = async (data: Record<string, any>) => {
    try {
      setIsSubmitting(true);
      const result = await callEntityAPI("update", data);
      if (result.error) {
        throw new Error(result.error);
      } else if (result.data !== undefined) {
        const updatedData =
          result.data && Object.keys(result.data).length > 0
            ? result.data
            : data;
        onSubmit(updatedData);
      } else {
        throw new Error("更新に失敗しました");
      }
    } catch (error) {
      console.error("Update request failed:", error);
      alert(
        `更新に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========================================
  // 初期値設定 (CreateDate, UpdateDate, Version)
  // ========================================

  useEffect(() => {
    const updates: Record<string, any> = {};

    // UserIDを設定
    if (
      userId &&
      entityMetadata?.fields.some((f) => f.protoName === "userId")
    ) {
      updates.userId = userId;
    }

    // ID生成
    if (entityMetadata?.idComponent) {
      let idTemplate = entityMetadata.idComponent;
      const templateRegex = /\{\{([^}]+)\}\}/g;
      const generatedId = idTemplate.replace(
        templateRegex,
        (match, fieldName) => {
          const field = entityMetadata.fields.find((f) => f.name === fieldName);
          if (field) {
            return (
              updates[field.protoName] || initialData[field.protoName] || ""
            );
          }
          switch (fieldName) {
            case "UUID":
              return generateUUIDv7();
            case "timestamp":
              return Date.now().toString();
            case "random":
              return Math.random().toString(36).substring(2, 15);
            default:
              return match;
          }
        }
      );
      updates.id = generatedId;
    }

    // 新規作成時の初期値設定
    if (mode === "create" && entityMetadata) {
      const now = new Date();

      entityMetadata.fields.forEach((field) => {
        switch (field.protoName) {
          case "id":
            // IDは上で処理済み
            break;
          case "createDate":
          case "updateDate":
            // 常に現在時刻で初期化（空でも埋める）
            if (!initialData[field.protoName]) {
              updates[field.protoName] = now;
            }
            break;
          case "version":
            // スキーマバージョン指定がtrueの時のみv1で初期化
            // falseの時はバックエンドで自動マイグレートされるので空のまま
            break;
          default:
            // その他のフィールドの初期化
            const structInfo = getEntityStructInfo(entityName);
            const structField = structInfo
              ? getEntityFieldsWithBase(structInfo).find(
                  (sf) => sf.protoName === field.protoName
                )
              : null;
            if (structField) {
              if (
                MasterDataSelectors.isMasterIdFieldByType(
                  structField.frontendType
                ) &&
                !updates[field.protoName] &&
                !initialData[field.protoName]
              ) {
                updates[field.protoName] = 0;
              } else if (
                isStructType(structField.type) &&
                !updates[field.protoName] &&
                !initialData[field.protoName]
              ) {
                updates[field.protoName] = {};
              }
            }
            break;
        }
      });
    }

    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  }, [userId, mode, normalizedEntityName, entityMetadata]);

  // initialDataが変更された時の処理
  useEffect(() => {
    if (Object.keys(initialData).length > 0) {
      const newData = { ...initialData };

      if (
        userId &&
        entityMetadata?.fields.some((f) => f.protoName === "userId")
      ) {
        newData.userId = userId;
      }

      // enum値を正規化
      if (entityMetadata) {
        entityMetadata.fields.forEach((field) => {
          if (
            isEnumType(field.type) &&
            newData[field.protoName] !== undefined
          ) {
            const currentValue = newData[field.protoName];
            if (typeof currentValue === "string") {
              const numValue = convertEnumNameToValue(currentValue, field.type);
              if (numValue !== null) {
                newData[field.protoName] = numValue;
              }
            }
          }
        });
      }

      // 新規作成時、CreateDate/UpdateDateが空なら初期値を設定
      // Versionはスキーマバージョン指定がtrueの時のみ設定（バックエンドで自動マイグレート）
      if (mode === "create") {
        const now = new Date();
        if (!newData.createDate) {
          newData.createDate = now;
        }
        if (!newData.updateDate) {
          newData.updateDate = now;
        }
      }

      setFormData(newData);
    }
  }, [mode, JSON.stringify(initialData), userId, normalizedEntityName]);

  // ID依存フィールドの変更時にIDを更新
  useEffect(() => {
    if (entityMetadata) {
      const idField = entityMetadata.fields.find((f) => f.protoName === "id");
      if (idField?.idDependencies && idField.idDependencies.length > 0) {
        const newId = generateID(idField.idDependencies, formData);
        if (formData.id !== newId) {
          setFormData((prev) => ({ ...prev, id: newId }));
        }
      }
    }
  }, [
    ...(entityMetadata?.fields
      .find((f) => f.protoName === "id")
      ?.idDependencies?.map((dep) => formData[dep]) || []),
    normalizedEntityName,
  ]);

  // ========================================
  // バリデーション・送信
  // ========================================

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!entityMetadata) return false;

    entityMetadata.fields.forEach((field) => {
      // IDフィールドはスキップ
      if (field.protoName === "id") return;
      // ルーティングからUserIDが渡されている場合はスキップ
      if (field.protoName === "userId" && userId) return;
      // excludeFieldsに含まれるフィールドはスキップ（外部で管理されている）
      if (excludeFields.includes(field.protoName)) return;
      // versionフィールドはバックエンドで自動設定されるのでスキップ
      if (field.protoName === "version") return;
      // createDate/updateDateも自動設定されるのでスキップ
      if (field.protoName === "createDate" || field.protoName === "updateDate")
        return;

      if (
        field.required &&
        (formData[field.protoName] === undefined ||
          formData[field.protoName] === null ||
          formData[field.protoName] === "")
      ) {
        newErrors[field.protoName] = `${field.name}は必須です`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("🔥 handleSubmit called", { mode, formData, delegateSubmit });
    const isValid = validateForm();
    console.log("🔥 validateForm result:", isValid, "errors:", errors);
    if (isValid) {
      if (mode === "create") {
        console.log("🔥 Showing confirm dialog");
        setShowConfirmDialog(true);
      } else {
        console.log("🔥 Calling submitForm directly");
        submitForm();
      }
    } else {
      console.log("🔥 Validation failed");
    }
  };

  const submitForm = () => {
    const submitData = { ...formData };

    if (submitData.userId && typeof submitData.userId === "string") {
      const userIdField = entityMetadata?.fields.find(
        (f) => f.protoName === "userId"
      );
      if (userIdField && userIdField.type === "number") {
        submitData.userId = parseInt(submitData.userId);
      }
    }

    entityMetadata?.fields.forEach((field) => {
      if (submitData[field.protoName]) {
        const isTimeField = field.type === "Date";
        if (isTimeField && typeof submitData[field.protoName] === "string") {
          const dateValue = new Date(submitData[field.protoName]);
          if (!isNaN(dateValue.getTime())) {
            submitData[field.protoName] = dateValue;
          }
        }
      }
    });

    // delegateSubmitがtrueの場合は、内部でAPIを呼び出さず、onSubmitにデータを渡す
    if (delegateSubmit) {
      setShowConfirmDialog(false);
      onSubmit(submitData);
      return;
    }

    if (mode === "create") {
      sendCreateRequest(submitData);
    } else if (mode === "edit") {
      sendUpdateRequest(submitData);
    } else {
      onSubmit(submitData);
    }
  };

  // ========================================
  // フィールド並び順
  // ========================================

  const getSortedFields = (fields: FieldMetadata[]): FieldMetadata[] => {
    const getFieldPriority = (field: FieldMetadata): number => {
      if (field.protoName === "id") return 1;
      if (field.protoName === "userId") return 2;
      if (
        field.protoName.endsWith("_id") ||
        field.type.endsWith("ID") ||
        field.protoName.endsWith("Id")
      )
        return 3;
      if (field.protoName === "createDate") return 997;
      if (field.protoName === "updateDate") return 998;
      if (field.protoName === "version") return 999;
      return 500;
    };

    return [...fields].sort(
      (a, b) => getFieldPriority(a) - getFieldPriority(b)
    );
  };

  // ========================================
  // レンダリング
  // ========================================

  if (!entityMetadata) {
    const availableEntities = structDocumentationData
      .filter(
        (struct) => struct.isEntity && struct.displayName.startsWith("entity.")
      )
      .map((struct) => struct.typeName)
      .join(", ");

    return (
      <div>
        <p>
          エンティティ "{entityName}" (正規化: "{normalizedEntityName}")
          のメタデータが見つかりません
        </p>
        <p>利用可能なエンティティ: {availableEntities}</p>
      </div>
    );
  }

  return (
    <div className="dynamic-form">
      <form onSubmit={handleSubmit} className="form">
        <div className="form-fields">
          {getSortedFields(entityMetadata.fields).map((field) => (
            <React.Fragment key={`fragment-${field.protoName}`}>
              {renderBeforeField[field.protoName]?.()}
              {/* Versionフィールドの前にスキーマバージョン指定フラグを表示 */}
              {field.protoName === "version" && (
                <div className="form-field skip-version-migration-field">
                  <label className="form-label">
                    スキーマバージョン指定
                    <span className="field-hint">
                      (有効にすると、以下のVersionの値をそのまま使用します)
                    </span>
                  </label>
                  <label className="form-toggle-label">
                    <div className="form-toggle-switch">
                      <input
                        type="checkbox"
                        checked={skipVersionMigration}
                        onChange={(e) =>
                          setSkipVersionMigration(e.target.checked)
                        }
                      />
                      <span className="form-toggle-slider"></span>
                    </div>
                    <span className="form-toggle-text">
                      {skipVersionMigration
                        ? "有効（Versionを指定値で保存）"
                        : "無効（最新バージョンに自動マイグレート）"}
                    </span>
                  </label>
                </div>
              )}
              {renderField(field)}
            </React.Fragment>
          ))}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "送信中..." : mode === "create" ? "作成" : "更新"}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="作成確認"
        message={`${entityMetadata.name || entityName}を作成しますか？`}
        confirmLabel="作成"
        cancelLabel="キャンセル"
        isLoading={isSubmitting}
        loadingLabel="作成中..."
        closeOnOverlayClick={closeDialogOnOverlayClick}
        onConfirm={submitForm}
        onCancel={() => setShowConfirmDialog(false)}
      >
        {renderConfirmSummary?.(formData)}
      </ConfirmDialog>
    </div>
  );
};
